import { FormEvent, useCallback, useEffect, useState } from 'react';
import { api, AdminUser, AdminUserDetail, Subject } from '../api';
import { Icon } from '../Icon';

/**
 * User administration.
 *
 * Suspension is offered first and deletion last, because they are not variants
 * of the same action: one is reversible and blocks access, the other destroys
 * contribution history and cannot be undone. The destructive one asks for the
 * account's email to be typed back, so it cannot happen on a misclick.
 *
 * Nothing here ever displays a password hash — the API does not return one.
 */
export function AdminUsers({ onBack }: { onBack: () => void }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<AdminUserDetail | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async (term: string) => {
    try {
      const d = await api.get<{ total: number; users: AdminUser[] }>(
        `/admin/users?limit=100&q=${encodeURIComponent(term)}`);
      setUsers(d.users); setTotal(d.total);
    } catch (e: any) { setErr(e.message); }
  }, []);

  useEffect(() => { void load(''); api.get<Subject[]>('/subjects').then(setSubjects).catch(() => {}); }, [load]);

  async function open(id: string) {
    setErr(null); setMsg(null);
    try { setSelected(await api.get<AdminUserDetail>(`/admin/users/${id}`)); }
    catch (e: any) { setErr(e.message); }
  }

  /** Every mutation refreshes both the detail and the list, so counts stay true. */
  async function act(fn: () => Promise<any>, ok: string) {
    setErr(null); setMsg(null);
    try {
      const updated = await fn();
      if (updated?.id) setSelected(updated);
      setMsg(ok);
      await load(q);
    } catch (e: any) { setErr(e.message); }
  }

  const field = {
    width: '100%', font: 'inherit', padding: '0.5rem', minHeight: '2.75rem',
    border: '1px solid var(--border-strong)', borderRadius: '8px',
    background: 'var(--surface)', color: 'var(--ink)',
  } as const;

  return (
    <>
      <button className="btn-ghost btn-back" onClick={onBack}><Icon name="arrow-left" />Back</button>
      <h1>Users</h1>
      <p className="muted">{total} account{total === 1 ? '' : 's'}.</p>

      {err && <div className="notice notice--warn" role="alert">{err}</div>}
      {msg && <div className="notice" role="status">{msg}</div>}

      <div className="btn-row" style={{ marginBottom: '1rem' }}>
        <label htmlFor="u-search" className="visually-hidden">Search accounts</label>
        <input id="u-search" value={q} placeholder="Search by email or name"
               onChange={(e) => { setQ(e.target.value); void load(e.target.value); }}
               style={{ ...field, maxWidth: '22rem' }} />
        <button onClick={() => { setCreating((c) => !c); setSelected(null); }}>
          {creating ? 'Cancel' : '+ New user'}
        </button>
      </div>

      {creating && (
        <CreateUser subjects={subjects} field={field}
                    onDone={async (created) => {
                      setCreating(false); setSelected(created);
                      setMsg(`Created ${created.email}. Give them the temporary password yourself — it is not emailed.`);
                      await load(q);
                    }}
                    onError={setErr} />
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="board">
          <thead>
            <tr>
              <th scope="col">Email</th><th scope="col">Name</th><th scope="col">Role</th>
              <th scope="col">Subjects</th><th scope="col">Status</th><th scope="col" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className={selected?.id === u.id ? 'is-me' : undefined}>
                <td>{u.email}</td>
                <td>{u.displayName ?? '—'}</td>
                <td>{u.role}</td>
                <td>{u.subjectCount}</td>
                <td>{u.suspended ? <strong>Suspended</strong> : 'Active'}</td>
                <td><button onClick={() => void open(u.id)}>Manage</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <UserDetail user={selected} subjects={subjects} field={field} act={act}
                    onClose={() => setSelected(null)}
                    onDeleted={async () => { setSelected(null); await load(q); }} />
      )}
    </>
  );
}

function CreateUser({ subjects, field, onDone, onError }: {
  subjects: Subject[]; field: any;
  onDone: (u: AdminUserDetail) => void; onError: (m: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('learner');
  const [password, setPassword] = useState('');
  const [packIds, setPackIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true);
    try {
      onDone(await api.post<AdminUserDetail>('/admin/users',
        { email, displayName, role, password, packIds }));
      setEmail(''); setDisplayName(''); setPassword(''); setPackIds([]);
    } catch (err: any) { onError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card">
      <h2 style={{ marginTop: 0 }}>New user</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        There is no email service, so you set a temporary password and pass it on yourself. They can
        change it from their profile once signed in.
      </p>

      <label htmlFor="n-email">Email</label>
      <input id="n-email" type="email" required value={email}
             onChange={(e) => setEmail(e.target.value)} style={field} />

      <label htmlFor="n-name" style={{ marginTop: '1rem' }}>Display name</label>
      <input id="n-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={field} />

      <label htmlFor="n-role" style={{ marginTop: '1rem' }}>Role</label>
      <select id="n-role" value={role} onChange={(e) => setRole(e.target.value)} style={field}>
        <option value="learner">learner — takes assessments</option>
        <option value="editor">editor — also moderates and publishes content</option>
        <option value="admin">admin — full administration</option>
      </select>

      <label htmlFor="n-pw" style={{ marginTop: '1rem' }}>Temporary password</label>
      <input id="n-pw" type="text" required value={password} minLength={10}
             onChange={(e) => setPassword(e.target.value)} style={field}
             aria-describedby="n-pw-help" />
      <p id="n-pw-help" className="muted" style={{ marginTop: '0.4rem' }}>
        At least 10 characters. Shown in clear text because you have to pass it on.
      </p>

      <fieldset style={{ marginTop: '1rem' }}>
        <legend>Subjects</legend>
        {subjects.map((s) => (
          <label key={s.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
            <input type="checkbox" checked={packIds.includes(s.id)}
                   onChange={(e) => setPackIds((cur) =>
                     e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id))} />
            <span>{s.subject}</span>
          </label>
        ))}
      </fieldset>

      <div className="btn-row" style={{ marginTop: '1.25rem' }}>
        <button className="btn-primary" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create user'}
        </button>
      </div>
    </form>
  );
}

function UserDetail({ user, subjects, field, act, onClose, onDeleted }: {
  user: AdminUserDetail; subjects: Subject[]; field: any;
  act: (fn: () => Promise<any>, ok: string) => Promise<void>;
  onClose: () => void; onDeleted: () => Promise<void>;
}) {
  const [name, setName] = useState(user.displayName ?? '');
  const [email, setEmail] = useState(user.email ?? '');
  const [role, setRole] = useState(user.role);
  const [packIds, setPackIds] = useState<string[]>(user.subjects.map((s) => s.id));
  const [reason, setReason] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');

  useEffect(() => {
    setName(user.displayName ?? ''); setEmail(user.email ?? ''); setRole(user.role);
    setPackIds(user.subjects.map((s) => s.id)); setReason(''); setNewPw(''); setConfirmDelete('');
  }, [user.id, user.suspended, user.role]);

  return (
    <section className="card" aria-labelledby="ud-heading" style={{ marginTop: '1.5rem' }}>
      <div className="btn-row" style={{ justifyContent: 'space-between' }}>
        <h2 id="ud-heading" style={{ marginTop: 0 }}>{user.email}</h2>
        <button onClick={onClose}>Close</button>
      </div>

      <p className="muted">
        Joined {new Date(user.createdAt).toLocaleDateString()} ·{' '}
        {user.lastLoginAt ? `last signed in ${new Date(user.lastLoginAt).toLocaleDateString()}` : 'never signed in'}
        {user.suspended && <> · <strong>Suspended: {user.suspendedReason}</strong></>}
      </p>

      <ul className="muted" style={{ marginTop: 0 }}>
        <li>{user.activity.attempts} assessments taken · {user.activity.cookies} cookies</li>
        <li>{user.activity.disputesAccepted} of {user.activity.disputes} reports accepted</li>
        <li>{user.activity.contributionsAccepted} of {user.activity.contributions} questions accepted</li>
      </ul>

      <h3>Account</h3>
      <label htmlFor="d-email">Email</label>
      <input id="d-email" value={email} onChange={(e) => setEmail(e.target.value)} style={field} />
      <label htmlFor="d-name" style={{ marginTop: '0.75rem' }}>Display name</label>
      <input id="d-name" value={name} onChange={(e) => setName(e.target.value)} style={field} />
      <label htmlFor="d-role" style={{ marginTop: '0.75rem' }}>Role</label>
      <select id="d-role" value={role} onChange={(e) => setRole(e.target.value)} style={field}>
        <option value="learner">learner</option>
        <option value="editor">editor</option>
        <option value="admin">admin</option>
      </select>
      <div className="btn-row" style={{ marginTop: '0.75rem' }}>
        <button className="btn-primary"
                onClick={() => act(() => api.put(`/admin/users/${user.id}`,
                  { email, displayName: name, role }), 'Account updated.')}>
          Save account
        </button>
      </div>

      <h3>Subjects</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        What this account can study. Removing them all leaves the catalogue empty until you give one back.
      </p>
      {subjects.map((s) => (
        <label key={s.id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <input type="checkbox" checked={packIds.includes(s.id)}
                 onChange={(e) => setPackIds((cur) =>
                   e.target.checked ? [...cur, s.id] : cur.filter((x) => x !== s.id))} />
          <span>{s.subject}</span>
        </label>
      ))}
      <div className="btn-row" style={{ marginTop: '0.75rem' }}>
        <button className="btn-primary"
                onClick={() => act(() => api.put(`/admin/users/${user.id}/subjects`, { packIds }),
                  'Subjects updated.')}>
          Save subjects
        </button>
      </div>

      <h3>Password</h3>
      <label htmlFor="d-pw">Set a new password</label>
      <input id="d-pw" type="text" value={newPw} minLength={10}
             onChange={(e) => setNewPw(e.target.value)} style={field} aria-describedby="d-pw-help" />
      <p id="d-pw-help" className="muted" style={{ marginTop: '0.4rem' }}>
        This signs the account out everywhere. Give them the new password yourself.
      </p>
      <div className="btn-row">
        <button disabled={newPw.length < 10}
                onClick={() => act(async () => {
                  await api.put(`/admin/users/${user.id}/password`, { password: newPw });
                  setNewPw(''); return null;
                }, 'Password set and all sessions signed out.')}>
          Set password
        </button>
      </div>

      <h3>Access</h3>
      {user.suspended ? (
        <div className="btn-row">
          <button className="btn-primary"
                  onClick={() => act(() => api.post(`/admin/users/${user.id}/reinstate`, {}),
                    'Account reinstated.')}>
            Reinstate account
          </button>
        </div>
      ) : (
        <>
          <label htmlFor="d-reason">Reason for suspending</label>
          <input id="d-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                 style={field} aria-describedby="d-reason-help" />
          <p id="d-reason-help" className="muted" style={{ marginTop: '0.4rem' }}>
            Required. Suspension blocks sign-in immediately and ends any session they already have.
            It is reversible and destroys nothing.
          </p>
          <div className="btn-row">
            <button disabled={!reason.trim()}
                    onClick={() => act(() => api.post(`/admin/users/${user.id}/suspend`, { reason }),
                      'Account suspended and signed out.')}>
              Suspend account
            </button>
          </div>
        </>
      )}

      <h3>Delete permanently</h3>
      <div className="notice notice--warn">
        <p style={{ marginTop: 0 }}>
          <strong>This cannot be undone.</strong> It destroys their reports, contributed questions,
          cookies and subject assignments. Their assessments are kept but detached from any name.
          If you only need to block access, suspend instead.
        </p>
        <label htmlFor="d-confirm">Type <code>{user.email}</code> to confirm</label>
        <input id="d-confirm" value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)}
               style={field} />
        <div className="btn-row" style={{ marginTop: '0.75rem' }}>
          <button disabled={confirmDelete !== user.email}
                  onClick={async () => {
                    await act(() => api.del(`/admin/users/${user.id}`), 'Account deleted.');
                    await onDeleted();
                  }}>
            Delete this account
          </button>
        </div>
      </div>
    </section>
  );
}
